import type { ColumnDef, Row } from "@tanstack/react-table";
import * as React from "react";
import { Download, Mail } from "lucide-react";
import { useAction, useQuery } from "convex/react";
import { useSearchParams } from "react-router-dom";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSalesOrdersSummary, type SalesOrderScope } from "@/hooks/useOrders";
import { BADGE_TONES } from "@/lib/badge-tones";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import {
  exportToCSV,
  formatCurrencyForExport,
  formatDateForExport,
  type ExportColumn,
} from "@/lib/export";
import {
  formatCurrency,
  formatDate,
  formatDeliveryMethod,
  formatStatusLabel,
} from "@/lib/format";
import { isHiddenInInternalOrderTablesByDefault, normalizeOrderStatus } from "@/lib/order-status-visibility";
import { hasPermission } from "@/lib/roles";
import { getSalesRepBadgeClass } from "@/lib/sales-tag-colors";
import {
  getOrderStatusBadgeClass,
  getPaymentStatusBadgeClass,
} from "@/lib/status-tag-colors";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { matchesScopedFilter, parseDateQueryParam } from "@/pages/admin/components/adminTabFilterUtils";
import { m } from "@/paraglide/messages.js";

type SalesOrder = {
  _id: string;
  _creationTime: number;
  createdAt?: number | null;
  orderNumber?: string;
  userId?: string;
  customerName: string;
  assignedSalesRepId: string;
  assignedSalesRepName: string;
  user?: {
    name?: string | null;
    email?: string | null;
    company?: string | null;
  } | null;
  isEnterprise?: boolean;
  orderPlacement?: {
    kind: "customer_self_service" | "sales_assisted";
    placedAt: number;
    placedBy?: unknown;
    salesDeviceInstallationId?: string;
  } | null;
  status: string;
  itemsCount: number;
  totalAmount?: number | null;
  paymentStatus: string;
  paymentMethod?: string;
  attachments?: any[];
  documentsCount?: number;
  items?: any[];
  delivery?: {
    deliveryMethod?: string;
    trackingNumber?: string;
    trackingUrl?: string;
  } | null;
};

const paymentMethodFilterOptions = [
  { label: "ACH / Bank Account", value: "us_bank_account" },
  { label: "Card", value: "card" },
  { label: "Credit (Card)", value: "credit (card)" },
  { label: "Credit (Bank)", value: "credit (bank)" },
  { label: "Credit", value: "credit" },
  { label: "PayPal", value: "paypal" },
  { label: "Wire", value: "wire" },
  { label: "Check", value: "check" },
  { label: "Cheque", value: "cheque" },
  { label: "Cash", value: "cash" },
] as const;

function getDeliveryMethodBadgeClass(deliveryMethod: unknown): string {
  const normalized = String(deliveryMethod ?? "").trim().toLowerCase();
  if (!normalized) return BADGE_TONES.slate;
  if (normalized.includes("pickup")) return BADGE_TONES.sky;
  if (normalized.includes("delivery") || normalized.includes("ship")) return BADGE_TONES.violet;
  if (normalized.includes("freight")) return BADGE_TONES.cyan;
  return BADGE_TONES.slate;
}

function formatPaymentMethodLabel(method: string): string {
  const normalized = method.toLowerCase();
  if (normalized === "us_bank_account" || normalized === "ach") return "ACH";
  if (normalized === "credit (card)") return "Credit (Card)";
  if (normalized === "credit (bank)") return "Credit (ACH)";
  if (normalized === "credit") return "Credit";
  if (normalized === "card") return "Card";
  if (normalized === "paypal") return "PayPal";
  if (normalized === "cheque" || normalized === "check") return "Check";
  if (normalized === "wire") return "Wire";
  if (normalized === "cash") return "Cash";
  return method.charAt(0).toUpperCase() + method.slice(1);
}

function getPaymentMethodBadgeClass(paymentMethod: unknown): string {
  const normalized = String(paymentMethod ?? "").trim().toLowerCase();
  if (!normalized) return BADGE_TONES.slate;
  if (normalized === "credit (card)" || normalized === "credit (bank)" || normalized === "credit") {
    return BADGE_TONES.zinc;
  }
  if (normalized.includes("card")) return BADGE_TONES.slate;
  if (normalized.includes("bank") || normalized.includes("ach") || normalized === "us_bank_account") {
    return BADGE_TONES.zinc;
  }
  if (normalized.includes("paypal")) return BADGE_TONES.orange;
  if (normalized.includes("wire")) return BADGE_TONES.zinc;
  if (normalized.includes("check") || normalized.includes("cheque")) return BADGE_TONES.amber;
  if (normalized.includes("cash")) return BADGE_TONES.slate;
  return BADGE_TONES.muted;
}

function SalesOrderItemsExpanded({ row }: { row: Row<SalesOrder> }) {
  const items = row.original.items ?? [];

  if (items.length === 0) {
    return (
      <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
        No line items available.
      </div>
    );
  }

  return (
    <div className="w-full space-y-2 px-4 md:px-6">
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(120px,180px)_72px_108px_120px_130px] gap-3 rounded-md border bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span className="min-w-0">Product</span>
        <span className="truncate">Cargo</span>
        <span className="truncate">Qty</span>
        <span className="truncate">Weight</span>
        <span className="truncate">Unit Price</span>
        <span className="truncate">Line Total</span>
      </div>
      <div className="space-y-1">
        {items.map((item: any, index: number) => {
          const quantity = Number(item.quantity ?? 0);
          const unitPrice = Number(item.unitPrice ?? 0);
          const unitWeight = Number(item.unitWeight ?? 0);
          const itemWeight = quantity * unitWeight;
          const lineTotal = quantity * unitPrice * unitWeight;

          return (
            <div
              key={item._id ?? `${item.product?.name ?? "product"}-${item.cargo?.ociNumber ?? "cargo"}-${index}`}
              className="grid grid-cols-[minmax(0,1fr)_minmax(120px,180px)_72px_108px_120px_130px] gap-3 rounded-md px-3 py-2 text-sm"
            >
              <span className="min-w-0 whitespace-normal break-words">{item.product?.name ?? "-"}</span>
              <span className="min-w-0 truncate">{item.cargo?.ociNumber ?? "-"}</span>
              <span className="min-w-0">{quantity}</span>
              <span className="min-w-0">{itemWeight > 0 ? itemWeight.toFixed(2) : "-"}</span>
              <span className="min-w-0">{formatCurrency(unitPrice)}</span>
              <span className="min-w-0">{formatCurrency(lineTotal)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getSalesOrderCreatedAt(order: Pick<SalesOrder, "createdAt" | "_creationTime">): number {
  const createdAt = Number(order.createdAt ?? order._creationTime);
  return Number.isFinite(createdAt) ? createdAt : 0;
}

function getPlacementActorLabel(actor: unknown): string {
  if (!actor || typeof actor !== "object") return "";
  if ("label" in actor && typeof actor.label === "string") {
    return actor.label.trim();
  }
  return "";
}

function OrderPlacementCell({ order }: { order: SalesOrder }) {
  const placement = order.orderPlacement;
  if (!placement) {
    return (
      <Badge variant="outline" className={BADGE_TONES.slate}>
        Legacy
      </Badge>
    );
  }

  if (placement.kind === "customer_self_service") {
    return (
      <div className="space-y-1">
        <Badge variant="outline" className={BADGE_TONES.sky}>
          Customer placed
        </Badge>
        <p className="whitespace-nowrap text-[11px] text-muted-foreground">
          {formatDate(placement.placedAt, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </p>
      </div>
    );
  }

  const actorLabel = getPlacementActorLabel(placement.placedBy) || "Sales";
  const deviceSuffix = placement.salesDeviceInstallationId?.slice(-6);
  return (
    <div className="space-y-1">
      <Badge variant="outline" className={BADGE_TONES.violet}>
        Sales-assisted
      </Badge>
      <p className="whitespace-nowrap text-[11px] text-muted-foreground">
        {actorLabel} ·{" "}
        {formatDate(placement.placedAt, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })}
      </p>
      {deviceSuffix ? (
        <p className="font-mono text-[10px] text-muted-foreground">Device …{deviceSuffix}</p>
      ) : null}
    </div>
  );
}

function useColumns(
  onSendInvoice: (order: SalesOrder) => void,
  sendingInvoiceIds: Set<string>,
  salesRepFilterOptions: Array<{ label: string; value: string }>,
  canSendInvoice: boolean,
): ColumnDef<SalesOrder>[] {
  return React.useMemo(
    () => [
      {
        id: "orderNumber",
        accessorKey: "orderNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Order #" />,
        cell: ({ row }) => (
          <span className="font-mono text-xs font-medium">
            {row.original.orderNumber || "—"}
          </span>
        ),
        enableColumnFilter: true,
        meta: { label: "Order", placeholder: "Search order...", variant: "text" as const },
      },
      {
        id: "isEnterprise",
        accessorFn: (row) => Boolean(row.isEnterprise),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Enterprise" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getEnterpriseBadgeClass(Boolean(row.original.isEnterprise))}>
            {getEnterpriseBadgeLabel(Boolean(row.original.isEnterprise))}
          </Badge>
        ),
        enableColumnFilter: true,
        meta: {
          label: "Enterprise",
          variant: "select" as const,
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
      },
      {
        id: "customer",
        accessorFn: (row) => row.user?.company || row.user?.name || row.customerName || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Customer" />,
        cell: ({ row }) => {
          const company = String(row.original.user?.company ?? "").trim();
          const customerName = String(row.original.user?.name ?? row.original.customerName ?? "").trim();
          const primary = company || customerName || "—";
          const secondary = company && customerName && company !== customerName ? customerName : "";

          return (
            <div className="flex flex-col">
              <span className="text-xs font-medium">{primary}</span>
              {secondary ? (
                <span className="text-xs text-muted-foreground">{secondary}</span>
              ) : null}
            </div>
          );
        },
        enableColumnFilter: true,
        meta: { label: "Customer", placeholder: "Search customer...", variant: "text" as const },
      },
      {
        id: "assignedSalesRepId",
        accessorKey: "assignedSalesRepId",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Sales Rep" />,
        cell: ({ row }) => {
          const salesRepName = String(row.original.assignedSalesRepName ?? "").trim() || "Unassigned";
          return (
            <Badge variant="outline" className={getSalesRepBadgeClass(salesRepName)}>
              {salesRepName}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: "Sales Rep",
          variant: "multiSelect" as const,
          options: salesRepFilterOptions,
        },
      },
      {
        id: "orderPlacement",
        accessorFn: (row) => row.orderPlacement?.kind ?? "legacy",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Placed By" />,
        cell: ({ row }) => <OrderPlacementCell order={row.original} />,
        enableColumnFilter: true,
        meta: {
          label: "Placed By",
          variant: "select" as const,
          options: [
            { label: "Customer placed", value: "customer_self_service" },
            { label: "Sales-assisted", value: "sales_assisted" },
            { label: "Legacy", value: "legacy" },
          ],
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge
              variant="outline"
              className={getOrderStatusBadgeClass(status)}
            >
              {formatStatusLabel(status)}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: "Status",
          variant: "select" as const,
          options: [
            { label: "Pending Payment", value: "pending_payment" },
            { label: "Payment Processing", value: "payment_processing" },
            { label: "Awaiting Inventory", value: "awaiting_inventory" },
            { label: "Ready to Prepare", value: "ready_to_prepare" },
            { label: "Ready for Pickup", value: "ready_for_pickup" },
            { label: "In Transit", value: "in_transit" },
            { label: "Delivered", value: "delivered" },
            { label: "On Hold", value: "on_hold" },
            { label: "Cancelled", value: "cancelled" },
          ],
        },
      },
      {
        id: "paymentStatus",
        accessorKey: "paymentStatus",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Payment" />,
        cell: ({ row }) => {
          const paymentStatus = row.original.paymentStatus;
          return (
            <Badge
              variant="outline"
              className={getPaymentStatusBadgeClass(paymentStatus)}
            >
              {formatStatusLabel(paymentStatus)}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: "Payment",
          variant: "select" as const,
          options: [
            { label: "Paid", value: "paid" },
            { label: "Pending", value: "pending" },
            { label: "Partial", value: "partial" },
            { label: "Overdue", value: "overdue" },
            { label: "Failed", value: "failed" },
          ],
        },
      },
      {
        id: "itemsCount",
        accessorKey: "itemsCount",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Qty" />,
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">{row.original.itemsCount}</span>
        ),
        meta: { label: "Qty", variant: "number" as const },
      },
      {
        id: "totalAmount",
        accessorKey: "totalAmount",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Total" />,
        cell: ({ row }) => {
          const totalAmount = row.original.totalAmount;
          return (
            <span className="text-sm font-medium tabular-nums">
              {typeof totalAmount === "number" ? formatCurrency(totalAmount) : "—"}
            </span>
          );
        },
        meta: { label: "Total", variant: "number" as const },
      },
      {
        id: "paymentMethod",
        accessorFn: (row) => String(row.paymentMethod ?? "").toLowerCase(),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Method" />,
        cell: ({ row }) => {
          const paymentMethod = String(row.original.paymentMethod ?? "").trim();
          if (!paymentMethod) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }

          return (
            <Badge variant="outline" className={getPaymentMethodBadgeClass(paymentMethod)}>
              {formatPaymentMethodLabel(paymentMethod)}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: "Method",
          variant: "select" as const,
          options: [...paymentMethodFilterOptions],
        },
      },
      {
        id: "delivery",
        accessorFn: (row) => row.delivery?.deliveryMethod ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Delivery" />,
        cell: ({ row }) => {
          const deliveryMethod = row.original.delivery?.deliveryMethod;
          if (!deliveryMethod) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <Badge variant="outline" className={getDeliveryMethodBadgeClass(deliveryMethod)}>
              {formatDeliveryMethod(deliveryMethod)}
            </Badge>
          );
        },
        enableColumnFilter: true,
        meta: { label: "Delivery", placeholder: "Delivery", variant: "text" as const },
      },
      {
        id: "_creationTime",
        accessorFn: getSalesOrderCreatedAt,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Created" />,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {formatDate(getSalesOrderCreatedAt(row.original))}
          </span>
        ),
        enableColumnFilter: true,
        meta: { label: "Created", variant: "date" as const },
      },
      {
        id: "invoices",
        header: "Invoices",
        cell: ({ row }) => {
          const orderId = String(row.original._id ?? "").trim();
          if (!orderId) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }

          return (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => window.open(`/invoice/${orderId}/customer`, "_blank", "noopener,noreferrer")}
            >
              Customer
            </Button>
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          if (!canSendInvoice) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }

          const order = row.original;
          const isSending = sendingInvoiceIds.has(order._id);
          return (
            <Button
              size="xs"
              variant="outline"
              className="h-7 gap-1.5"
              disabled={isSending}
              onClick={() => onSendInvoice(order)}
            >
              <Mail className="size-3.5" />
              {isSending ? "Sending..." : "Send Invoice"}
            </Button>
          );
        },
        enableSorting: false,
        meta: { label: "Actions" },
      },
    ],
    [canSendInvoice, onSendInvoice, salesRepFilterOptions, sendingInvoiceIds],
  );
}

interface SalesOrdersTableProps {
  showPageHeader?: boolean;
  scopeToSearchParams?: boolean;
  orderScope?: SalesOrderScope;
}

function isOrderVisibleForScope(order: SalesOrder, orderScope: SalesOrderScope): boolean {
  const normalizedStatus = normalizeOrderStatus(order.status);

  if (orderScope === "abandoned") {
    return isHiddenInInternalOrderTablesByDefault(normalizedStatus);
  }

  if (orderScope === "both") {
    return normalizedStatus !== "cancelled";
  }

  return normalizedStatus !== "cancelled" && !isHiddenInInternalOrderTablesByDefault(normalizedStatus);
}

export function SalesOrdersTable({
  showPageHeader = true,
  scopeToSearchParams = false,
  orderScope,
}: SalesOrdersTableProps) {
  const { authUserId, userRole } = useAuthIdentity();
  const [searchParams] = useSearchParams();
  const canSendInvoice = hasPermission(userRole, "sales:edit");
  const clientIdParam = scopeToSearchParams ? searchParams.get("clientId") : null;
  const productIdParam = scopeToSearchParams ? searchParams.get("productId") : null;
  const cargoSearchParam = scopeToSearchParams ? searchParams.get("cargoSearch") : null;
  const contractSearchParam = scopeToSearchParams ? searchParams.get("contractSearch") : null;
  const scopedClientId = clientIdParam && clientIdParam !== "all-users" ? clientIdParam : undefined;
  const scopedProductId = productIdParam && productIdParam !== "all-products" ? productIdParam : undefined;
  const scopedCargoSearch = cargoSearchParam && cargoSearchParam !== "all-cargos" ? cargoSearchParam : undefined;
  const scopedContractSearch = contractSearchParam && contractSearchParam !== "all-contracts" ? contractSearchParam : undefined;
  const scopedDeliveredOnly = scopeToSearchParams && searchParams.get("deliveredOnly") === "true";
  const lightOrders = useSalesOrdersSummary(
    authUserId,
    scopedClientId,
    scopeToSearchParams ? 500 : 100,
    scopeToSearchParams
      ? {
          ...(orderScope ? { orderScope } : {}),
          productId: scopedProductId,
          cargoSearch: scopedCargoSearch,
          contractSearch: scopedContractSearch,
          deliveredOnly: scopedDeliveredOnly,
        }
      : undefined,
  );
  const viewerSalesRepFilterKeys = useQuery(
    (api.sales as any).getViewerSalesRepFilterKeys,
    authUserId ? { authUserId } : "skip",
  ) as string[] | undefined;
  const [visibleOrderIds, setVisibleOrderIds] = React.useState<string[]>([]);
  const orderDetails = useQuery(
    api.sales.getOrdersSummaryDetails,
    authUserId && visibleOrderIds.length > 0
      ? { authUserId, orderIds: visibleOrderIds as any }
      : "skip",
  ) as Record<string, Partial<SalesOrder>> | undefined;
  const orders = React.useMemo(
    () =>
      (lightOrders ?? []).map((order: any) => ({
        ...order,
        ...(orderDetails?.[String(order._id)] ?? {}),
      })),
    [lightOrders, orderDetails],
  );
  const sendInvoiceAction = useAction(api.orders.sendInvoiceEmailAction);
  const [sendingInvoiceIds, setSendingInvoiceIds] = React.useState<Set<string>>(new Set());
  const tableData = React.useMemo(
    () =>
      (orders as SalesOrder[]).filter((order) => {
        if (orderScope) {
          if (!isOrderVisibleForScope(order, orderScope)) return false;
        } else if (isHiddenInInternalOrderTablesByDefault(normalizeOrderStatus(order.status))) {
          return false;
        }
        if (scopedDeliveredOnly && normalizeOrderStatus(order.status) !== "delivered") return false;
        return true;
      }),
    [orders, orderScope, scopedDeliveredOnly],
  );
  const orderNumberParam = scopeToSearchParams ? searchParams.get("orderNumber") : null;
  const companyParam = scopeToSearchParams ? searchParams.get("company") : null;
  const statusParam = scopeToSearchParams ? searchParams.get("status") : null;
  const fromParam = scopeToSearchParams ? searchParams.get("from") : null;
  const toParam = scopeToSearchParams ? searchParams.get("to") : null;
  const displayTableData = React.useMemo(() => {
    if (!scopeToSearchParams) return tableData;

    const fromMs = parseDateQueryParam(fromParam);
    const toMs = parseDateQueryParam(toParam, true);
    const normalizedStatusParam = String(statusParam ?? "").trim().toLowerCase();

    return tableData.filter((order) => {
      if (!matchesScopedFilter([order.orderNumber], orderNumberParam)) return false;

      if (
        !scopedClientId &&
        !matchesScopedFilter(
          [order.user?.company, order.user?.name, order.user?.email, order.customerName],
          companyParam,
        )
      ) {
        return false;
      }

      if (normalizedStatusParam && normalizeOrderStatus(order.status) !== normalizedStatusParam) {
        return false;
      }

      const createdAtMs = getSalesOrderCreatedAt(order);
      if (fromMs !== null && (!Number.isFinite(createdAtMs) || createdAtMs < fromMs)) return false;
      if (toMs !== null && (!Number.isFinite(createdAtMs) || createdAtMs > toMs)) return false;

      return true;
    });
  }, [
    companyParam,
    fromParam,
    orderNumberParam,
    scopeToSearchParams,
    scopedClientId,
    statusParam,
    tableData,
    toParam,
  ]);
  const salesRepFilterOptions = React.useMemo(
    () => {
      const optionsByValue = new Map<string, { label: string; value: string }>();

      for (const order of displayTableData) {
        const value = String(order.assignedSalesRepId ?? "").trim();
        const label = String(order.assignedSalesRepName ?? "").trim() || "Unassigned";
        if (!value || optionsByValue.has(value)) continue;
        optionsByValue.set(value, { label, value });
      }

      return Array.from(optionsByValue.values()).sort((left, right) => {
        if (left.value === "unassigned") return 1;
        if (right.value === "unassigned") return -1;
        return left.label.localeCompare(right.label);
      });
    },
    [displayTableData],
  );
  const matchingViewerSalesRepFilterKeys = React.useMemo(() => {
    if (!viewerSalesRepFilterKeys || viewerSalesRepFilterKeys.length === 0) {
      return [];
    }

    const availableSalesRepFilterKeys = new Set(
      salesRepFilterOptions.map((option) => option.value.toLowerCase()),
    );

    return viewerSalesRepFilterKeys.filter((key) =>
      availableSalesRepFilterKeys.has(String(key).toLowerCase()),
    );
  }, [salesRepFilterOptions, viewerSalesRepFilterKeys]);

  const initialColumnFilters = React.useMemo(
    () => {
      if (userRole !== "sales" || matchingViewerSalesRepFilterKeys.length === 0) {
        return undefined;
      }

      return [{ id: "assignedSalesRepId", value: matchingViewerSalesRepFilterKeys }];
    },
    [userRole, matchingViewerSalesRepFilterKeys],
  );
  const isLoadingViewerDefaults = Boolean(authUserId) && (
    userRole === undefined ||
    (userRole === "sales" && viewerSalesRepFilterKeys === undefined)
  );

  const onSendInvoice = React.useCallback(
    async (order: SalesOrder) => {
      setSendingInvoiceIds((prev) => new Set(prev).add(order._id));
      try {
        const result = await sendInvoiceAction({ orderId: order._id as any });
        if ((result as any)?.success) {
          toast.success(
            (result as any)?.sentTo
              ? `Invoice email sent to ${(result as any).sentTo}`
              : "Invoice email sent successfully",
          );
        } else {
          toast.error((result as any)?.error || "Failed to send invoice email");
        }
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to send invoice email",
        );
      } finally {
        setSendingInvoiceIds((prev) => {
          const next = new Set(prev);
          next.delete(order._id);
          return next;
        });
      }
    },
    [sendInvoiceAction],
  );

  const columns = useColumns(
    onSendInvoice,
    sendingInvoiceIds,
    salesRepFilterOptions,
    canSendInvoice,
  );
  const exportColumns = React.useMemo<ExportColumn[]>(
    () => [
      { key: "orderNumber", label: "Order #" },
      {
        key: "isEnterprise",
        label: "Enterprise",
        format: (value: any) => (value ? "Enterprise" : "Standard"),
      },
      { key: "customerName", label: "Customer" },
      { key: "status", label: "Status" },
      { key: "paymentStatus", label: "Payment" },
      { key: "itemsCount", label: "Qty" },
      { key: "totalAmount", label: "Total", format: formatCurrencyForExport },
      {
        key: "paymentMethod",
        label: "Method",
        format: (value: any) => (value ? formatPaymentMethodLabel(String(value)) : ""),
      },
      {
        key: "deliveryLabel",
        label: "Delivery",
      },
      { key: "createdAtForExport", label: "Created", format: formatDateForExport },
    ],
    [],
  );

  const exportRows = React.useCallback(
    (rows: SalesOrder[], filename: string) => {
      if (rows.length === 0) {
        toast.error("No orders selected");
        return;
      }

      const rowsForExport = rows.map((row) => ({
        ...row,
        createdAtForExport: getSalesOrderCreatedAt(row),
        deliveryLabel: formatDeliveryMethod(row.delivery?.deliveryMethod),
      }));

      exportToCSV(rowsForExport, exportColumns, filename);
    },
    [exportColumns],
  );

  const handleExportAll = React.useCallback(() => {
    exportRows(
      displayTableData,
      `sales-orders-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, [exportRows, displayTableData]);

  const handleExportSelected = React.useCallback(
    (selectedRows: SalesOrder[]) => {
      exportRows(
        selectedRows,
        `sales-orders-selected-${new Date().toISOString().split("T")[0]}.csv`,
      );
    },
    [exportRows],
  );

  const pageHeader = showPageHeader ? (
    <div>
      <h1 className="text-3xl font-bold">{m.nav_orders()}</h1>
      <p className="text-muted-foreground">View all sales orders with your accounts prefiltered by default.</p>
    </div>
  ) : null;

  if (lightOrders === undefined || isLoadingViewerDefaults) {
    return (
      <div className="space-y-6">
        {pageHeader}
        <DataTableSkeleton columnCount={11} rowCount={8} />
      </div>
    );
  }

  return (
    <div className={showPageHeader ? "space-y-6" : undefined}>
      {pageHeader}

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold leading-none tracking-tight">Orders</h3>
            <p className="text-sm text-muted-foreground">
              All orders with payment management and status tracking.
            </p>
          </div>
          {displayTableData.length > 0 ? (
            <>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {displayTableData.length < tableData.length
                    ? `${displayTableData.length} of ${tableData.length} orders`
                    : `${displayTableData.length} ${displayTableData.length === 1 ? "order" : "orders"}`}
                </p>
                <Button variant="outline" size="sm" onClick={handleExportAll}>
                  <Download className="mr-2 h-4 w-4" />
                  Export All
                </Button>
              </div>
              <InternalEntityDataTable
                data={displayTableData}
                columns={columns}
                pageSize={20}
                initialSorting={[{ id: "_creationTime", desc: true }]}
                initialColumnFilters={initialColumnFilters}
                onVisibleRowsChange={setVisibleOrderIds}
                actionBar={({ selectedRows, clearSelection }) => (
                  <DataTableFloatingBar
                    selectedCount={selectedRows.length}
                    onClear={clearSelection}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExportSelected(selectedRows)}
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                      Export Selected
                    </Button>
                  </DataTableFloatingBar>
                )}
                expandedRow={{
                  render: ({ row }) => <SalesOrderItemsExpanded row={row} />,
                  getCanExpand: (order) => (order.itemsCount ?? 0) > 0,
                }}
              />
            </>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {tableData.length > 0
                ? "No orders match the current filters. Clear filters to see all orders."
                : "No orders yet. New orders will appear here as they're placed."}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SalesOrdersPage() {
  return <SalesOrdersTable />;
}
